/**
 * 路由守卫 —— 09 章 §9.2.4 的三条硬规则。
 *
 * 1. 前端权限只管"看得见/看不见"，不是安全边界
 * 2. **无权限 → `/403` 页面，不是 toast**。toast 会让用户以为"再点一次就好了"
 * 3. 按钮级权限用 `v-perm` 指令，无权限**直接移除节点**而非 disabled——
 *    一个 disabled 的「标记完成」按钮会让 trainer 反复追问为什么点不了
 */

import type { Router } from 'vue-router'

import { useAuthStore } from '@/stores/auth'
import { useBrandingStore } from '@/stores/branding'
import { useMenuStore } from '@/stores/menu'
import { usePermissionStore } from '@/stores/permission'

export function installGuards(router: Router): void {
  router.beforeEach(async (to) => {
    if (to.meta.publicRoute) return true

    const auth = useAuthStore()
    if (!auth.token) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    if (!auth.loaded) {
      try {
        // 单飞（store 内部保证），所以多个并发导航不会打多次 `/me`
        await auth.fetchMe()
      } catch {
        // token 在 localStorage 里但已失效（后端重启换了 SECRET_KEY、
        // 或口令被重置导致 token_version 递增）。清掉并回登录页。
        auth.reset()
        return { name: 'login', query: { redirect: to.fullPath } }
      }
    }

    // 菜单来自数据库，它同时决定**有哪些路由**。所以要在权限判定之前拉，
    // 否则第一次导航到一个动态页面时路由表里还没有它。
    //
    // 注册完必须重试这次导航：`addRoute()` 不会重新解析正在进行的导航，
    //   直接放行的话，刷新一个动态页面会命中 404，而再点一次就好了 ——
    //   这种「第一次不行第二次行」的毛病最难查。
    if (await useMenuStore().ensureLoaded(router)) {
      return to.fullPath
    }

    // 首登强制改密：除了改密页与登录页，其它一律拦到个人设置。
    // 这一条必须在权限判定**之前**——否则一个 must_change_password 的 viewer
    // 会先被 403 拦掉，永远走不到改密页。
    if (auth.mustChangePassword && to.name !== 'profile') {
      return { name: 'profile', query: { force: 'password' } }
    }

    if (!usePermissionStore().has(to.meta.perms)) {
      return { name: 'forbidden' }
    }

    // 不在下发菜单树里的页面，直链也不放行：`admin_role_menu` 与 `superadmin_only`
    // 只影响 `/menus/me` 下发，而 `routes.ts` 静态注册的路由永远都在。
    //
    // 两类路由受约束：`meta.menu === true` 的菜单页按自己的 route_name 判；
    // 带 `meta.parent` 的子页面（详情 / 纳管 / 新建）按父菜单判 —— 父菜单被裁掉时
    // 子页面一并拦下，否则「服务器」设成仅超管之后租户管理员照样能直链进 `/servers/:id`。
    // 降级态（/menus/me 挂了）不拦 —— 那套本来就回退到 routes.ts + perms，
    // 按半份数据拦人会把好账号也锁在门外。superadmin 不拦：他不走这两道筛选。
    const menu = useMenuStore()
    if (menu.loaded && !auth.isSuperadmin && typeof to.name === 'string') {
      if (to.meta.menu === true && !menu.allowedRouteNames.has(to.name)) {
        // 登录默认落点（/overview）被关掉时，带去他菜单里的第一项 ——
        // 直接 403 会让每个受限角色的第一眼都是一句报错。
        if (to.name === 'overview' && menu.firstRouteName) {
          return { name: menu.firstRouteName }
        }
        return { name: 'forbidden' }
      }
      if (typeof to.meta.parent === 'string' && !menu.allowedRouteNames.has(to.meta.parent)) {
        return { name: 'forbidden' }
      }
    }

    return true
  })

  router.afterEach((to) => {
    // 页面自己定标题的就别插手。这里是**异步**赋值（等品牌 store 就绪），
    // 晚于页面的 `onMounted`，不短路的话页面写什么都会被盖掉（台账 AG-159）。
    if (to.meta.ownTitle) return
    // 品牌字样可定制：等 store 就绪再定标题（幂等，加载后立即 resolve）。
    const branding = useBrandingStore()
    void branding.ensureLoaded().then(() => {
      document.title = to.meta.title ? `${to.meta.title} · ${branding.brandText}` : branding.brandText
    })
  })
}
